import Button from "@/components/Button";
import Icon from "@/components/Icon";
import SearchButton from "@/components/SearchButton";
import { ToolbarQuickSearchButton } from "@/components/SearchButton";
import TopActionBar from "@/components/TopActionBar";
import useCapabilities, {
  DOMAIN,
  PERMISSION,
  useCanManageInteractionsBool,
  useCanManagePersonNotes,
  useCanManageTasksBool,
  useCanViewTasksBool,
  useDenyAuthorization,
  useHasCapabilityBool
} from "@/hooks/useCapabilities";
import { usePersonProfileReadCapabilities } from "@/features/person/hooks/usePersonProfileReadCapabilities";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import useCurrentUser from "@/hooks/useCurrentUser";
import useEffectiveDisplayUser from "@/hooks/useEffectiveDisplayUser";
import { Path, useNavigate } from "@/router";
import { hrefWithStoredDefaultTab } from "@/lib/defaultTab";
import { generatePath } from "react-router";
import { useLocation, useParams } from "react-router-dom";
import { SnackbarProvider } from "notistack";
import { snackbarProviderComponents } from "@/components/Snackbar";
import GlobalSearch from "@/components/GlobalSearch";
import useGlobalSearchShortcut from "@/hooks/useGlobalSearchShortcut";
import { buildGlobalSearchDestinations } from "@/lib/globalSearchDestinations";
import { canShowGrpFinanceChrome } from "@/pages/billing/finance/_utils";
import { buildGlobalSearchResolveContext } from "@/lib/globalSearchResolve";
import { canAccessAgentChatLogs } from "@/features/agent-chat-logs/access";
import { canAccessM360Configuration, useCanViewInteractionTopicsTab } from "@/pages/configuration/access";
import { usePatResultVolumeAccess } from "@/pages/events/pat-results/access";
import { CreateGlobalInteractionSideSheet } from "./CreateGlobalInteraction/CreateGlobalInteractionSideSheet";
import CreateGlobalNoteSideSheet from "./CreateGlobalNote/CreateGlobalNoteSideSheet";
import CreateGlobalTaskSideSheet from "./CreateGlobalTask/CreateGlobalTaskSideSheet";
import Menu, { type MenuItem } from "@/components/Menu";
import { ActionShortcutTooltip, type KeyboardShortcutHintProps } from "@/components/KeyboardShortcutHint";
import Sidebar from "./Sidebar";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import ActiveCallSideSheet from "./IncomingCallEventIntegration/ActiveCallSideSheet";
import IncomingCallEventPersonModal from "./IncomingCallEventIntegration/IncomingCallEventPersonModal";
import TopActionBarProvider from "./TopActionBarProvider";
import { SideBar, useTopActionBar } from "./TopActionBarContext";
import ErrorBannerProvider from "@/providers/ErrorBannerProvider";
import { NavLink } from "./types";
import { buildPrimaryNavTabItems, buildGoNavChordItems } from "./primaryNavTabItems";
import useGoNavChordMode from "@/hooks/useGoNavChordMode";
import { useQuestionMarkChatShortcut, useSlashShortcut } from "@/hooks/usePageSearchShortcut";
import { buildKeyboardShortcutEntries } from "@/lib/keyboardShortcutCatalog";
import classNames from "classnames";
import ScrollToTop from "@/components/ScrollToTop";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import useOpenGlobalAddMenuShortcut from "@/hooks/useOpenGlobalAddMenuShortcut";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePrimarySection } from "@/hooks/usePrimarySection";
import useAmplitude from "@/hooks/useAmplitude";
import { GLOBAL_SEARCH_ANALYTICS_EVENTS } from "./globalSearchAmplitude";
import { openTrackedGlobalAddMenuItem } from "@/lib/quickActionMenuAmplitude";
import { canAccessMcpPlayground } from "@/features/mcp-playground/access";
import AssumeUserBanner from "@/components/AssumeUserBanner";
import LocalBackendUnavailableBanner from "./LocalBackendUnavailableBanner";
import { GlobalQuickActionMenu } from "@/components/QuickActionMenu";
import { RosterAccessibleSectionsProvider } from "@/features/roster-activity";

const PRIMARY_NAV_DOMAINS = [DOMAIN.EVERYONE];

const GLOBAL_ADD_SHORTCUT_HINTS: KeyboardShortcutHintProps[] = [
  { keyLabel: "a", showModifier: false, testId: "global-add-menu-shortcut-hint" }
];

type AppLayoutProps<P extends Path> = {
  interior?: boolean;
  navLinks?: Record<string, NavLink<P>[]> | NavLink<P>[];
  BannerComponent?: React.ReactNode;
  bottomBar?: boolean;
  /** Pin the shell to the visible viewport so page chrome cannot scroll away. */
  lockViewport?: boolean;
};

function Main<P extends Path>({
  navLinks,
  BannerComponent,
  interior,
  bottomBar,
  lockViewport = false,
  children
}: React.PropsWithChildren<AppLayoutProps<P>>) {
  const { logout } = useCurrentUser();
  const user = useEffectiveDisplayUser();
  const { isCompactLayout } = useIsCompactLayout();
  const { showSidebar, toggleSidebar, currentSidebar } = useTopActionBar();
  const canManageInteractions = useCanManageInteractionsBool();
  const canManagePersonNotes = useCanManagePersonNotes();
  const canManageTasks = useCanManageTasksBool();
  const canViewTasks = useCanViewTasksBool();
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  const { loaded, capabilities, hasRole, hasUniversityAccess, universityLegacyIds } = useCapabilities();
  const denyAuthorization = useDenyAuthorization();
  const isUniversityStaff = hasRole(ROLE.UNIVERSITY_STAFF);
  const isPortalStaff = isUniversityStaff || hasRole(ROLE.PGA_MAGAZINE_STAFF);
  const showStaffChrome = loaded && !isPortalStaff;
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const showActivitiesScreen = !!flags["backoffice-activities-screen"];
  const { hasPrimarySection, primarySectionId } = usePrimarySection();
  const { trackEvent } = useAmplitude();
  const canViewPeople = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  const canViewFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.viewFacilities
  });
  const {
    canViewPersonMembership,
    canViewPersonEducation,
    canViewPersonCareer,
    canViewCareerPlan,
    canViewCareerPreferences,
    canViewCompensationProfiles,
    canManageJobPosts,
    canViewJobPosts,
    canViewPersonEvents,
    canViewPersonGroups,
    canViewPersonChangeHistory,
    canViewPersonDocuments,
    canViewPersonBilling,
    canViewOrders,
    canManageApplications,
    canViewCommittees
  } = usePersonProfileReadCapabilities();
  const canViewMcpPlayground = canAccessMcpPlayground(canViewPeople, canViewFacilities);
  const canViewChatLogs = canAccessAgentChatLogs(hasRole(ROLE.GLOBAL_ADMIN));
  const canViewM360Configuration = canAccessM360Configuration(hasRole(ROLE.GLOBAL_ADMIN));
  const canViewInteractionTopics = useCanViewInteractionTopicsTab();
  const { canView: canViewPatResultVolume } = usePatResultVolumeAccess();

  const openMcpPlayground = useCallback(() => {
    navigate("/dev/chat" as Path);
  }, [navigate]);

  useSlashShortcut();
  useQuestionMarkChatShortcut(canViewMcpPlayground ? openMcpPlayground : undefined);

  const openGlobalAddMenuItem = useCallback(
    (dataTestId: string, openSidebar: () => void) => openTrackedGlobalAddMenuItem(dataTestId, openSidebar, trackEvent),
    [trackEvent]
  );

  const openGlobalInteraction = useCallback(() => {
    openGlobalAddMenuItem("add-interaction-menu-item", showSidebar(SideBar.CREATE_GLOBAL_INTERACTION))();
  }, [openGlobalAddMenuItem, showSidebar]);
  const openGlobalNote = useCallback(() => {
    openGlobalAddMenuItem("add-note-menu-item", showSidebar(SideBar.CREATE_GLOBAL_NOTE))();
  }, [openGlobalAddMenuItem, showSidebar]);
  const openGlobalTask = useCallback(() => {
    openGlobalAddMenuItem("add-task-menu-item", showSidebar(SideBar.CREATE_GLOBAL_TASK))();
  }, [openGlobalAddMenuItem, showSidebar]);

  const [globalAddMenuOpen, setGlobalAddMenuOpen] = useState(false);
  const [primaryNavMenuOpen, setPrimaryNavMenuOpen] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const globalSearchOpenRef = useRef(false);

  const setGlobalSearchOpenState = useCallback((open: boolean) => {
    globalSearchOpenRef.current = open;
    setGlobalSearchOpen(open);
  }, []);

  const openGlobalSearch = useCallback(() => {
    if (!globalSearchOpenRef.current) {
      trackEvent(GLOBAL_SEARCH_ANALYTICS_EVENTS.clickGlobalSearch);
    }
    setGlobalSearchOpenState(true);
  }, [setGlobalSearchOpenState, trackEvent]);

  const toggleGlobalSearch = useCallback(() => {
    const next = !globalSearchOpenRef.current;
    if (next) {
      trackEvent(GLOBAL_SEARCH_ANALYTICS_EVENTS.clickGlobalSearch);
    }
    setGlobalSearchOpenState(next);
  }, [setGlobalSearchOpenState, trackEvent]);

  useGlobalSearchShortcut({
    onToggle: toggleGlobalSearch,
    enabled: showStaffChrome
  });

  const addInteractionLabel = isCompactLayout ? "Interaction" : "Add Interaction";
  const addNoteLabel = isCompactLayout ? "Note" : "Add Note";
  const addTaskLabel = isCompactLayout ? "Task" : "Add Task";

  const globalAddMenuItems = useMemo((): MenuItem[] => {
    return [
      ...(canManageInteractions
        ? [
            {
              label: addInteractionLabel,
              iconName: "3p" as const,
              onClick: openGlobalInteraction,
              dataTestId: "add-interaction-menu-item",
              chordKey: "i",
              useDivider: false
            }
          ]
        : []),
      ...(canManagePersonNotes
        ? [
            {
              label: addNoteLabel,
              iconName: "sticky_note_2" as const,
              onClick: openGlobalNote,
              dataTestId: "add-note-menu-item",
              chordKey: "n",
              useDivider: false
            }
          ]
        : []),
      ...(canManageTasks
        ? [
            {
              label: addTaskLabel,
              iconName: "inventory" as const,
              onClick: openGlobalTask,
              dataTestId: "add-task-menu-item",
              chordKey: "t",
              useDivider: false
            }
          ]
        : [])
    ];
  }, [
    addInteractionLabel,
    addNoteLabel,
    addTaskLabel,
    canManageInteractions,
    canManagePersonNotes,
    canManageTasks,
    openGlobalInteraction,
    openGlobalNote,
    openGlobalTask
  ]);

  const openGlobalAddMenu = useCallback(() => {
    if (globalAddMenuItems.length === 1) {
      globalAddMenuItems[0].onClick?.();
      return;
    }

    if (globalAddMenuItems.length > 1) {
      setGlobalAddMenuOpen(true);
    }
  }, [globalAddMenuItems]);

  useOpenGlobalAddMenuShortcut({
    onOpenAddMenu: openGlobalAddMenu,
    enabled: globalAddMenuItems.length > 0
  });

  useEffect(() => {
    if (!loaded) return;

    if (hasRole(ROLE.UNIVERSITY_STAFF)) {
      if (location.pathname.startsWith("/education/universities/")) {
        // Check if the user has access to this specific university

        // Get the university ID from the pathname
        const requestedUniversityId = location.pathname.split("/")[3];

        // Deny access if the user does not have access to this university
        if (!hasUniversityAccess(requestedUniversityId)) {
          if (universityLegacyIds.length > 0) {
            const universityId = universityLegacyIds[0];
            denyAuthorization(
              generatePath("/education/universities/:universityId/students", {
                universityId
              }) as Path
            );
          } else {
            denyAuthorization();
          }
        }
        // exit normally, allowing access to the university page
      } else {
        denyAuthorization();
      }
    }
  }, [loaded, hasRole, denyAuthorization, location.pathname, hasUniversityAccess, universityLegacyIds]);

  function createActions() {
    let actions = [];

    // Only show quick search for known HQ staff (not university or magazine portal staff)
    if (showStaffChrome) {
      actions.push(
        <div key="toolbarQuickSearch" className="flex items-center">
          {isCompactLayout ? (
            <ToolbarQuickSearchButton onClick={openGlobalSearch} testId="quick-search-button" />
          ) : (
            <SearchButton onClick={openGlobalSearch} testId="quick-search-button" />
          )}
        </div>
      );
    }

    const menuItems = globalAddMenuItems;

    if (menuItems.length === 0) {
      return <>{actions}</>;
    }

    if (isCompactLayout) {
      actions.push(
        <div key="createGlobalAction">
          <GlobalQuickActionMenu
            menuItems={menuItems}
            menuOpen={globalAddMenuOpen}
            onMenuOpenChange={setGlobalAddMenuOpen}
            showOpenShortcutHint
          />
        </div>
      );
      return <>{actions}</>;
    }

    if (menuItems.length > 1) {
      const addButton = (
        <ActionShortcutTooltip label="Add" hints={GLOBAL_ADD_SHORTCUT_HINTS}>
          <Button
            variant="squared"
            icon="add"
            iconWeight={800}
            testId="create-action-button"
            ariaLabel="Add"
            ariaKeyShortcuts="a"
            ariaExpanded={globalAddMenuOpen}
          >
            <Icon name="expand_more" size={20} weight={800} />
          </Button>
        </ActionShortcutTooltip>
      );
      actions.push(
        <div key="createGlobalAction">
          <Menu
            AnchorComponent={addButton}
            menuItems={menuItems}
            open={globalAddMenuOpen}
            onOpenChange={setGlobalAddMenuOpen}
          />
        </div>
      );
    } else if (menuItems.length === 1) {
      actions.push(
        <div key="createGlobalAction">
          <ActionShortcutTooltip label="Add" hints={GLOBAL_ADD_SHORTCUT_HINTS}>
            <Button
              variant="squared"
              icon="add"
              iconWeight={800}
              onClick={menuItems[0].onClick}
              testId="create-action-button"
              ariaLabel="Add"
              ariaKeyShortcuts="a"
            />
          </ActionShortcutTooltip>
        </div>
      );
    }

    return <>{actions}</>;
  }

  const hasSecondaryNav =
    navLinks && (Array.isArray(navLinks) ? navLinks.length > 0 : Object.keys(navLinks).length > 0);
  const shouldRenderSidebar = !isCompactLayout && hasSecondaryNav;

  const sectionsLink =
    hasPrimarySection && primarySectionId
      ? (`/sections/${primarySectionId}${isCompactLayout ? "" : "/jobs/list"}` as Path)
      : ("/sections" as Path);

  const primaryNavTabItems = buildPrimaryNavTabItems({
    pathname: location.pathname,
    sectionsLink,
    showActivitiesScreen
  });

  const primaryNavDomains = PRIMARY_NAV_DOMAINS;

  const applyStoredDefaultTab = flags["backoffice-navigation-preferences"] === true;
  const goNavItems = useMemo(
    () => buildGoNavChordItems(primaryNavTabItems, primaryNavDomains, navigate, applyStoredDefaultTab),
    [primaryNavTabItems, primaryNavDomains, navigate, applyStoredDefaultTab]
  );

  const { goNavActive, deactivateGoNav } = useGoNavChordMode({
    items: goNavItems,
    enabled: showStaffChrome && goNavItems.length > 0,
    onCompactMenuOpen: isCompactLayout && showStaffChrome ? () => setPrimaryNavMenuOpen(true) : undefined,
    onCompactMenuClose: isCompactLayout && showStaffChrome ? () => setPrimaryNavMenuOpen(false) : undefined
  });

  const handlePrimaryNavMenuOpenChange = useCallback(
    (open: boolean) => {
      setPrimaryNavMenuOpen(open);
      if (!open) deactivateGoNav();
    },
    [deactivateGoNav]
  );

  const primaryNav =
    isCompactLayout && showStaffChrome
      ? {
          tabItems: primaryNavTabItems,
          domains: primaryNavDomains
        }
      : undefined;

  const primaryNavGoNav =
    showStaffChrome && goNavItems.length > 0
      ? {
          menuOpen: primaryNavMenuOpen,
          onMenuOpenChange: handlePrimaryNavMenuOpenChange,
          goNavActive
        }
      : undefined;

  const keyboardShortcutEntries = useMemo(
    () =>
      buildKeyboardShortcutEntries({
        showGoNav: showStaffChrome && goNavItems.length > 0,
        showGlobalSearch: showStaffChrome,
        addMenuItems: globalAddMenuItems.flatMap((item) =>
          item.chordKey ? [{ label: String(item.label), chordKey: item.chordKey }] : []
        ),
        canViewMcpPlayground,
        canViewTasks
      }),
    [canViewMcpPlayground, canViewTasks, globalAddMenuItems, goNavItems.length, showStaffChrome]
  );

  const globalSearchDestinations = useMemo(
    () =>
      buildGlobalSearchDestinations({
        scormCourses: !!flags["backoffice-scorm-courses"],
        activitiesScreen: !!flags["backoffice-activities-screen"],
        memberElections: !!flags["applications-membership"],
        canViewMcpPlayground,
        canViewChatLogs,
        canViewM360Configuration,
        canViewInteractionTopics,
        systemPreferences: flags["backoffice-system-preferences"] === true,
        prospectProgramTab: flagsLoaded && flags["backoffice-prospect-program-tab"] !== true,
        canViewPersonMembership,
        canViewPersonEducation,
        canViewPersonCareer,
        canViewCareerPlan,
        canViewCareerPreferences,
        canViewCompensationProfiles,
        canManageJobPosts,
        canViewJobPosts,
        canViewPersonEvents,
        canViewPersonGroups,
        canViewPersonChangeHistory,
        canViewPersonDocuments,
        canViewPersonBilling,
        canViewOrders,
        canViewGrpFinance: canShowGrpFinanceChrome(flags, capabilities),
        canManageApplications,
        canViewCommittees,
        canViewPatResultVolume: canViewPatResultVolume && flags["backoffice-pat-result-volume"] === true,
        personProfileDocuments: !!flags["backoffice-person-profile-documents"]
      }),
    [
      canManageApplications,
      canManageJobPosts,
      canViewCareerPlan,
      canViewCareerPreferences,
      canViewChatLogs,
      canViewCommittees,
      canViewCompensationProfiles,
      canViewInteractionTopics,
      canViewJobPosts,
      canViewM360Configuration,
      canViewMcpPlayground,
      canViewOrders,
      canViewPersonBilling,
      capabilities,
      canViewPersonCareer,
      canViewPersonEducation,
      canViewPersonEvents,
      canViewPersonGroups,
      canViewPersonChangeHistory,
      canViewPersonDocuments,
      canViewPersonMembership,
      canViewPatResultVolume,
      flags,
      flagsLoaded
    ]
  );

  const globalSearchContext = useMemo(
    () => buildGlobalSearchResolveContext(params, location.pathname),
    [location.pathname, params]
  );

  const handleGlobalSearchNavigate = useCallback(
    (path: string) => {
      setGlobalSearchOpenState(false);
      navigate(hrefWithStoredDefaultTab(path, applyStoredDefaultTab) as Path);
    },
    [navigate, setGlobalSearchOpenState, applyStoredDefaultTab]
  );

  return (
    <div
      className={classNames({
        "flex min-h-0 flex-1 flex-col overflow-hidden overscroll-none": lockViewport,
        "min-h-screen": !lockViewport
      })}
      data-testid={lockViewport ? "app-layout-lock-viewport" : undefined}
    >
      <ScrollToTop />
      <TopActionBar
        {...{ user, logout, interior, hasSecondaryNav }}
        actions={createActions()}
        primaryNav={primaryNav}
        primaryNavGoNav={primaryNavGoNav}
        keyboardShortcutEntries={keyboardShortcutEntries}
      />
      <AssumeUserBanner />
      <LocalBackendUnavailableBanner />
      {showStaffChrome ? (
        <GlobalSearch
          open={globalSearchOpen}
          onOpenChange={setGlobalSearchOpenState}
          destinations={globalSearchDestinations}
          resolveContext={globalSearchContext}
          onNavigate={handleGlobalSearchNavigate}
        />
      ) : null}
      <main
        className={classNames({
          "grid min-h-0 flex-1 grid-cols-[auto_1fr] overflow-hidden": shouldRenderSidebar && lockViewport,
          "grid grid-cols-[auto_1fr]": shouldRenderSidebar && !lockViewport,
          "flex min-h-0 flex-1 flex-col overflow-hidden": !shouldRenderSidebar && lockViewport,
          block: !shouldRenderSidebar && !lockViewport
        })}
      >
        {shouldRenderSidebar && <Sidebar navLinks={navLinks} bottomBar={bottomBar} />}
        <div
          className={classNames("w-full min-w-0", {
            "flex min-h-0 flex-1 flex-col overflow-hidden": lockViewport
          })}
        >
          <ErrorBannerProvider>
            {BannerComponent}
            <CreateGlobalInteractionSideSheet
              sidebarOpen={currentSidebar === SideBar.CREATE_GLOBAL_INTERACTION}
              setSidebarOpen={toggleSidebar(SideBar.CREATE_GLOBAL_INTERACTION)}
            />
            <CreateGlobalNoteSideSheet
              sidebarOpen={currentSidebar === SideBar.CREATE_GLOBAL_NOTE}
              setSidebarOpen={toggleSidebar(SideBar.CREATE_GLOBAL_NOTE)}
            />
            <CreateGlobalTaskSideSheet
              sidebarOpen={currentSidebar === SideBar.CREATE_GLOBAL_TASK}
              setSidebarOpen={toggleSidebar(SideBar.CREATE_GLOBAL_TASK)}
            />
            <IncomingCallEventPersonModal
              enabled={!!flags["backoffice-twilio-call-event-integration"] && showStaffChrome}
            />
            <ActiveCallSideSheet enabled={!!flags["backoffice-twilio-call-event-integration"] && showStaffChrome} />
            <div
              className={classNames({
                "flex min-h-0 flex-1 flex-col overflow-hidden": lockViewport,
                "mb-24": !isCompactLayout && !lockViewport,
                "mx-4 lg:mx-12 xl:mx-24": !shouldRenderSidebar && !lockViewport,
                "lg:mx-12 xl:mx-24": lockViewport && !isCompactLayout && !shouldRenderSidebar,
                "mr-4 lg:mr-12 xl:mr-24 ml-3 lg:ml-6 xl:ml-12": shouldRenderSidebar && !lockViewport
              })}
            >
              {interior && !shouldRenderSidebar ? <div className="max-w-[1440px] mx-auto">{children}</div> : children}
            </div>
          </ErrorBannerProvider>
        </div>
      </main>
    </div>
  );
}

export default function AppLayout<P extends Path>(props: React.PropsWithChildren<AppLayoutProps<P>>) {
  return (
    <TopActionBarProvider>
      <SnackbarProvider
        Components={snackbarProviderComponents}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
      >
        <RosterAccessibleSectionsProvider>
          <Main {...props} />
        </RosterAccessibleSectionsProvider>
      </SnackbarProvider>
    </TopActionBarProvider>
  );
}
